import { getMediaCompletion } from 'pages/Representatives/utils/getMediaCompletion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FileType } from 'shared/enums/FileType';
import { MediumableType } from 'shared/enums/MediumableType';
import { IRepresentativeSingleProfileMedia } from 'shared/interfaces/IRepresentativesEditProfile';
import { representativeService } from 'shared/services/representativesService';
import { ls } from 'shared/utils/ls';

const { getSingleRepresentativeAttachedMediaGQL, getSingleRepresentativeHiddendMediaGQL } = representativeService();

export const useEditProfileMediaDocumentData = (file_type: FileType) => {
  const { getLS } = ls();
  const user_id = JSON.parse(getLS('user')).id;
  const { id: profileId } = useParams() as { id: string };

  const {
    data: attachedMedia,
    isLoading: attachedMediaLoading,
    isError: attachedMediaError,
    refetch: attachedMediaRefetch,
    isFetching: attachedMediaFetching,
  } = getSingleRepresentativeAttachedMediaGQL({
    mediumable_id: profileId,
    mediumable_type: MediumableType.PROFILE,
    file_type: file_type,
  });

  const attached_media = useMemo(() => {
    return attachedMedia?.data?.medium_attachments_connection?.nodes || [];
  }, [attachedMedia]);

  const {
    data: hiddenMedia,
    isLoading: hiddenMediaLoading,
    isError: hiddenMediaError,
    refetch: hiddenMediaRefetch,
    isFetching: hiddenMediaFetching,
  } = getSingleRepresentativeHiddendMediaGQL({
    user_id: user_id,
    file_type: file_type,
    media_bin: true,
    profile_id: profileId,
  });

  const hidden_media = useMemo(() => {
    return hiddenMedia?.data?.media_connection?.nodes || [];
  }, [hiddenMedia]);

  const completeness = getMediaCompletion(
    attached_media?.length,
    file_type === FileType.IMAGE
      ? {
          0: 0,
          1: 33,
          2: 66,
        }
      : null,
  );

  const handleRefetch = useCallback(() => {
    if (attachedMediaError) {
      attachedMediaRefetch();
    } else {
      hiddenMediaRefetch();
    }
  }, [attachedMediaError, hiddenMediaError]);

  // Initial load loading
  const media_loading =
    attachedMediaLoading ||
    hiddenMediaLoading ||
    (attachedMediaFetching && !attachedMedia) ||
    (hiddenMediaFetching && !hiddenMedia);

  // Error
  const media_error = hiddenMediaError || attachedMediaError;

  return {
    attached_media,
    hidden_media,
    media_error,
    media_loading,
    completeness,
    handleRefetch,
    profileId,
  };
};
